import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';

const PasswordReset = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const baseUrl = 'https://api.homehive.co.zw';

  // Get the path parameters from the URL
  const pathname = location.pathname;
  const pathParts = pathname.split('/');
  const uidb64 = pathParts[pathParts.length - 2];
  const token = pathParts[pathParts.length - 1];

  // Validate URL parameters on component mount
  useEffect(() => {
    console.log('Path parts:', pathParts); // Debug log
    console.log('Parameters:', { uidb64, token }); // Debug log
    console.log('Full URL:', window.location.href); // Debug full URL

    if (!uidb64 || !token || uidb64 === 'reset-password' || token === 'reset-password') {
      Swal.fire({
        icon: 'error',
        title: 'Invalid Reset Link',
        text: 'The password reset link appears to be invalid or incomplete. Please check your email for the correct link.',
        confirmButtonColor: '#1a237e'
      }).then(() => {
        navigate('/login');
      });
    }
  }, [uidb64, token, navigate, pathParts]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters long';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    // Validate URL parameters before submission
    if (!uidb64 || !token || uidb64 === 'reset-password' || token === 'reset-password') {
      Swal.fire({
        icon: 'error',
        title: 'Invalid Reset Link',
        text: 'The password reset link appears to be invalid or incomplete. Please check your email for the correct link.',
        confirmButtonColor: '#1a237e'
      });
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${baseUrl}/api/password/reset/confirm/`, {
        new_password: formData.password,
        uidb64: uidb64,
        token: token
      });

      Swal.fire({
        icon: 'success',
        title: 'Password Reset Successful!',
        text: 'Your password has been successfully reset. Please login with your new password.',
        confirmButtonColor: '#1a237e'
      }).then(() => {
        navigate('/login');
      });
    } catch (error) {
      console.error('Reset error:', error.response || error);
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.message || 
                          error.response?.data?.error ||
                          'Failed to reset password. Please try again.';
      
      Swal.fire({
        icon: 'error',
        title: 'Reset Failed',
        text: errorMessage,
        confirmButtonColor: '#1a237e'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Return early if missing required parameters or if they're invalid
  if (!uidb64 || !token || uidb64 === 'reset-password' || token === 'reset-password') {
    return null;
  }

  const styles = {
    container: {
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)',
      padding: '20px'
    },
    formCard: {
      background: 'white',
      padding: '40px',
      borderRadius: '10px',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      width: '100%',
      maxWidth: '400px'
    },
    title: {
      textAlign: 'center',
      color: '#1a237e',
      marginBottom: '30px',
      fontSize: '24px',
      fontWeight: 'bold'
    },
    formGroup: {
      marginBottom: '20px'
    },
    label: {
      display: 'block',
      marginBottom: '8px',
      color: '#333',
      fontSize: '14px',
      fontWeight: '500'
    },
    input: {
      width: '100%',
      padding: '12px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      fontSize: '16px',
      transition: 'border-color 0.3s ease'
    },
    error: {
      color: '#dc3545',
      fontSize: '12px',
      marginTop: '5px'
    },
    button: {
      width: '100%',
      padding: '14px',
      background: '#1a237e',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      fontSize: '16px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      opacity: isLoading ? 0.7 : 1
    },
    passwordRequirements: {
      fontSize: '12px',
      color: '#666',
      marginTop: '5px'
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.formCard}>
        <h2 style={styles.title}>Reset Your Password</h2>
        
        <form onSubmit={handleSubmit}>
          <div style={styles.formGroup}>
            <label style={styles.label} htmlFor="password">
              New Password
            </label>
            <input
              type="password"
              id="password"
              name="password"
              style={{
                ...styles.input,
                borderColor: errors.password ? '#dc3545' : '#ddd'
              }}
              value={formData.password}
              onChange={handleChange}
              placeholder="Enter your new password"
            />
            {errors.password && (
              <div style={styles.error}>{errors.password}</div>
            )}
            <div style={styles.passwordRequirements}>
              Password must be at least 8 characters long
            </div>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label} htmlFor="confirmPassword">
              Confirm Password
            </label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              style={{
                ...styles.input,
                borderColor: errors.confirmPassword ? '#dc3545' : '#ddd'
              }}
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Confirm your new password"
            />
            {errors.confirmPassword && (
              <div style={styles.error}>{errors.confirmPassword}</div>
            )}
          </div>

          <button
            type="submit"
            style={styles.button}
            disabled={isLoading}
          >
            {isLoading ? 'Resetting Password...' : 'Reset Password'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default PasswordReset;
